import { z } from "zod";
import type { ProjectMetadataField } from "@/types/api";

export function createMetadataSchema(
  fields: ProjectMetadataField[],
): z.ZodType<Record<string, unknown>> {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const field of fields) {
    const required = field.required;
    let fieldSchema: z.ZodTypeAny;
    switch (field.field_type) {
      case "boolean":
        fieldSchema = z.boolean().optional();
        break;
      case "number":
        fieldSchema = z
          .union([z.number(), z.string()])
          .transform((v) => (v === "" || v === null || v === undefined ? undefined : Number(v)))
          .refine(
            (v) => v === undefined || (typeof v === "number" && !Number.isNaN(v)),
            { message: "Must be a number" },
          );
        if (required) {
          fieldSchema = fieldSchema.refine((v) => v !== undefined, {
            message: "Required",
          });
        }
        break;
      case "multi_select":
        fieldSchema = z.array(z.string());
        if (required) {
          fieldSchema = (fieldSchema as z.ZodArray<z.ZodString>).min(1, {
            message: "Required",
          });
        }
        break;
      case "date":
      case "text":
      case "single_select":
      default:
        fieldSchema = z.string();
        if (required) {
          fieldSchema = (fieldSchema as z.ZodString).min(1, { message: "Required" });
        } else {
          fieldSchema = fieldSchema.optional();
        }
    }
    shape[field.id] = fieldSchema;
  }
  shape.__tags = z.array(z.string());
  shape.__plan = z.string().nullable();
  shape.__pinX = z.number().nullable();
  shape.__pinY = z.number().nullable();
  shape.__pinBearing = z.number().nullable();
  return z.object(shape) as z.ZodType<Record<string, unknown>>;
}
